import { useState } from 'react';
import { trpc } from '@/lib/trpc/trpc';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ActiveUserPicker from '@/components/elements/userPicker/activeUserPicker';
import { SmartDatetimeInput } from '@/components/ui/datetime-input';

interface CalendarBookingFormProps {
  bookingId?: string;
  userId?: string;
  date?: Date | undefined | null;
  bookingStart?: Date | undefined | null;
  bookingEnd?: Date | undefined | null;
  comment?: string;
  parentShow?: (open: boolean) => void;
}

export default function CalendarBookingForm({
  ...props
}: CalendarBookingFormProps) {
  const trpcUtils = trpc.useUtils();
  const { toast } = useToast();

  const addCalendarBooking = trpc.Booking.CreateBooking.useMutation({
    onSuccess: async () => {
      toast({
        title: 'Booking Added Successfully',
        description: 'Successfully created booking',
      });
      await trpcUtils.Booking.invalidate();
      if (props.parentShow) {
        props.parentShow(false);
      }
    },
    onError: async (error) => {
      toast({
        title: 'Error Adding Booking',
        description: error.message,
        variant: 'destructive',
      });
      await trpcUtils.Booking.invalidate();
    },
  });

  const updateCalendarBooking = trpc.Booking.UpdateBooking.useMutation({
    onSuccess: async () => {
      toast({
        title: 'Booking Updated Successfully',
        description: 'Successfully updated booking',
      });
      await trpcUtils.Booking.invalidate();
      if (props.parentShow) {
        props.parentShow(false);
      }
    },
    onError: async (error) => {
      toast({
        title: 'Error Updating Booking',
        description: error.message,
        variant: 'destructive',
      });
      await trpcUtils.Booking.invalidate();
    },
  });

  const calendarBookingFormSchema = z.object({
    date: z.date(),
    bookingStart: z.date(),
    bookingEnd: z.date(),
    comment: z.string().optional(),
    userId: z.string(),
  });

  // Lazily initialise so the impure Date call runs once, not on every render.
  const [defaultDate] = useState(() => new Date());

  const calendarBookingForm = useForm<
    z.infer<typeof calendarBookingFormSchema>
  >({
    resolver: zodResolver(calendarBookingFormSchema),
    defaultValues: {
      date: defaultDate,
      bookingStart: props.bookingStart || undefined,
      bookingEnd: props.bookingEnd || undefined,
      userId: props.userId,
      comment: props.comment ?? '',
    },
  });

  const calendarBookingFormHandler = (
    values: z.infer<typeof calendarBookingFormSchema>
  ) => {
    if (props.bookingId) {
      updateCalendarBooking.mutate({
        bookingId: props.bookingId,
        date: values.date,
        bookingStart: values.bookingStart,
        bookingEnd: values.bookingEnd,
        comment: values.comment || '',
        userId: values.userId,
      });
    } else {
      addCalendarBooking.mutate({
        date: values.date,
        bookingStart: values.bookingStart,
        bookingEnd: values.bookingEnd,
        comment: values.comment || '',
        userId: values.userId,
      });
    }
  };

  return (
    <Form {...calendarBookingForm}>
      <form
        onSubmit={calendarBookingForm.handleSubmit(calendarBookingFormHandler)}
        onReset={() => calendarBookingForm.reset()}
      >
        <div className='grid grid-cols-1 gap-3 py-4'>
          <FormField
            control={calendarBookingForm.control}
            name='bookingStart'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Start</FormLabel>
                <FormControl>
                  <SmartDatetimeInput
                    value={field.value}
                    onValueChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={calendarBookingForm.control}
            name='bookingEnd'
            render={({ field }) => (
              <FormItem>
                <FormLabel>End</FormLabel>
                <FormControl>
                  <SmartDatetimeInput
                    value={field.value}
                    onValueChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={calendarBookingForm.control}
            name='comment'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Comment?</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={calendarBookingForm.control}
            name='userId'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Member</FormLabel>
                <FormControl>
                  <ActiveUserPicker
                    selectedUser={field.value}
                    selectedUserChanged={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <br />
        <FormMessage />
        <div className='space-x-3'>
          <Button type='submit'>Save Booking</Button>
          <Button type='reset' variant={'destructive'}>
            Clear
          </Button>
        </div>
      </form>
    </Form>
  );
}
